import { useNavigate } from "react-router-dom";
import { ArrowRight, Target, Zap, Users, CheckCircle, TrendingUp, Award, Compass } from "lucide-react";
import "@/styles/landing.css";

const STEPS = [
  { num: "01", title: "Identify Skill Gaps", desc: "AI Diagnostics analyze your resume against 10M+ job descriptions", icon: Target },
  { num: "02", title: "Execute Learning", desc: "Personalized roadmaps that adapt as you complete certifications", icon: TrendingUp },
  { num: "03", title: "Direct Placement", desc: "Connect directly with high-growth roles that match your skills", icon: Award },
];

const LAYERS = [
  {
    layer: "Layer 01",
    title: "Career Intelligence",
    desc: "An AI engine that analyzes your current profile against 10M+ job descriptions to find your \"Unfair Advantage\" and immediate skill gaps.",
    features: ["ATS Strength Analysis", "Market Salary Benchmarking"],
  },
  {
    layer: "Layer 02",
    title: "The Engine",
    desc: "Not just a to-do list, but a dynamic roadmap that adjusts in real-time as you complete certifications, projects, and interview simulations.",
    features: ["Adaptive Learning Paths", "Verified Skill Proofs"],
  },
  {
    layer: "Layer 03",
    title: "The Marketplace",
    desc: "We connect your verified profile directly to hiring managers who value skill signals over traditional credentials. Zero noise.",
    features: ["Priority Shortlisting", "Direct Referral Network"],
  },
];

const VALUES = [
  {
    icon: Zap,
    title: "Outcome First",
    desc: "We don't measure success by app usage or course completions. We measure it by job offers signed and salary uplift achieved.",
  },
  {
    icon: Users,
    title: "Truth Over Flattery",
    desc: "We tell you the hard truth about your resume and skills. Accurate feedback is the only way to beat a competitive job market.",
  },
];

export default function AboutPage() {
  const navigate = useNavigate();

  return (
    <div className="ap-root" data-testid="about-page">

      {/* ============ HERO ============ */}
      <section className="ap-mesh ap-grid-overlay relative pt-24 pb-10" style={{ overflowX: "clip" }}>
        <div className="ap-section text-center max-w-[760px] mx-auto">
          <span className="ap-chip ap-rise ap-rise-1" style={{ background: "var(--ap-violet-soft)", borderColor: "transparent", color: "var(--ap-violet)" }}>
            <Compass className="w-4 h-4" /> About PlacementOS
          </span>
          <h1 className="ap-display font-extrabold mt-5 text-[var(--ap-ink)] ap-rise ap-rise-2" style={{ fontSize: "clamp(34px,5vw,64px)", lineHeight: 1.04, letterSpacing: "-0.03em" }}>
            The Operating System for<br /><span className="ap-grad-text">Professional Success.</span>
          </h1>
          <p className="text-[17px] md:text-[18px] text-[var(--ap-ink-2)] leading-relaxed mt-5 max-w-[600px] mx-auto ap-rise ap-rise-3">
            We believe career mobility shouldn't be accidental. We're building the infrastructure that converts individual ambition into measurable employability.
          </p>
        </div>
      </section>

      {/* ============ MISSION + STATS ============ */}
      <section className="ap-section py-10">
        <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-14 items-center">
          <div>
            <span className="ap-eyebrow">Our mission</span>
            <h2 className="ap-h2 mt-3" style={{ fontSize: "clamp(26px,3.4vw,40px)" }}>
              Bridging the gap between <span className="ap-grad-text">potential &amp; placement</span>.
            </h2>
            <p className="text-[15.5px] text-[var(--ap-muted)] leading-relaxed mt-5">
              The modern job search is broken. Students graduate with degrees but without direction. Professionals have skills but no signal. Legacy platforms profit from the volume of applications, not the success of the candidate.
            </p>
            <p className="text-[15.5px] text-[var(--ap-muted)] leading-relaxed mt-4">
              PlacementOS was founded to solve this information asymmetry. We provide the intelligence layer that tells you exactly what you need to learn, how to prove it, and where it's valued.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="ap-card ap-pop p-7 text-center">
              <div className="ap-display text-[44px] font-extrabold ap-grad-text leading-none">42%</div>
              <p className="text-[13.5px] font-medium text-[var(--ap-muted)] mt-2.5">Employability Index Improvement</p>
            </div>
            <div className="ap-card ap-pop p-7 text-center">
              <div className="ap-display text-[44px] font-extrabold text-[var(--ap-indigo)] leading-none">100M+</div>
              <p className="text-[13.5px] font-medium text-[var(--ap-muted)] mt-2.5">Resumes Stalled in India</p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section className="ap-section py-10">
        <div className="text-center max-w-[640px] mx-auto">
          <span className="ap-eyebrow">The process</span>
          <h2 className="ap-h2 mt-3">How <span className="ap-grad-text">PlacementOS</span> works.</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {STEPS.map((s) => (
            <div key={s.num} className="ap-card ap-pop p-7 relative">
              <span className="ap-display text-[44px] font-extrabold text-[var(--ap-canvas-2)] absolute top-4 right-5 select-none">{s.num}</span>
              <span className="w-12 h-12 rounded-2xl grid place-items-center mb-4" style={{ background: "var(--ap-violet-soft)", color: "var(--ap-violet)" }}>
                <s.icon className="w-6 h-6" />
              </span>
              <h3 className="ap-display font-bold text-[19px] text-[var(--ap-ink)]">{s.title}</h3>
              <p className="text-[14.5px] text-[var(--ap-muted)] leading-relaxed mt-2">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ============ ECOSYSTEM LAYERS ============ */}
      <section className="ap-section py-10">
        <div className="ap-card ap-mesh overflow-hidden p-8 md:p-12">
          <div className="text-center max-w-[640px] mx-auto">
            <span className="ap-eyebrow">The ecosystem</span>
            <h2 className="ap-h2 mt-3" style={{ fontSize: "clamp(26px,3.4vw,42px)" }}>Inside the <span className="ap-grad-text">ecosystem</span>.</h2>
            <p className="text-[15px] text-[var(--ap-muted)] leading-relaxed mt-3">Our three-layered approach ensures you stay ahead of the hiring curve.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {LAYERS.map((item) => (
              <div key={item.layer} className="ap-card ap-pop p-6">
                <p className="ap-eyebrow text-[var(--ap-violet)] mb-3">{item.layer}</p>
                <h3 className="ap-display font-bold text-[19px] text-[var(--ap-ink)]">{item.title}</h3>
                <p className="text-[14px] text-[var(--ap-muted)] leading-relaxed mt-2.5">{item.desc}</p>
                <ul className="mt-5 space-y-2.5">
                  {item.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-[14px] text-[var(--ap-ink-2)]">
                      <CheckCircle className="w-[18px] h-[18px] text-[var(--ap-green)] shrink-0 mt-0.5" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ VALUES ============ */}
      <section className="ap-section py-10">
        <div className="text-center max-w-[640px] mx-auto">
          <span className="ap-eyebrow">What we stand for</span>
          <h2 className="ap-h2 mt-3">Our <span className="ap-grad-text">values</span>.</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6 mt-12">
          {VALUES.map((v) => (
            <div key={v.title} className="ap-card ap-pop p-8">
              <span className="w-12 h-12 rounded-2xl grid place-items-center mb-5" style={{ background: "var(--ap-violet-soft)", color: "var(--ap-violet)" }}>
                <v.icon className="w-6 h-6" />
              </span>
              <h3 className="ap-display font-bold text-[21px] text-[var(--ap-ink)]">{v.title}</h3>
              <p className="text-[15px] text-[var(--ap-muted)] leading-relaxed mt-3">{v.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="ap-section pb-24 pt-10">
        <div className="text-center overflow-hidden py-16 md:py-20 px-6 rounded-[28px]" style={{ background: "var(--ap-fire)", boxShadow: "0 30px 70px -28px rgba(107,92,230,.5)" }}>
          <h2 className="ap-display font-extrabold text-white" style={{ fontSize: "clamp(28px,4.2vw,46px)", lineHeight: 1.05, letterSpacing: "-0.02em" }}>
            Ready to upgrade your career trajectory?
          </h2>
          <p className="text-white/85 mt-3.5 text-[16px] md:text-[17px] max-w-[600px] mx-auto">
            Join 15,247 ambitious professionals using PlacementOS to gain clarity and secure higher-paying roles.
          </p>
          <div className="flex flex-col sm:flex-row gap-3.5 justify-center mt-8">
            <button
              onClick={() => navigate("/")}
              className="ap-btn ap-btn-lg bg-white shadow-[0_12px_30px_-10px_rgba(0,0,0,.35)] hover:-translate-y-0.5"
              style={{ color: "var(--ap-violet)" }}
            >
              Start Diagnostic <ArrowRight className="w-[18px] h-[18px]" />
            </button>
            <button
              onClick={() => navigate("/contact")}
              className="ap-btn ap-btn-lg text-white bg-white/10 border border-white/40 hover:bg-white/20"
            >
              Talk to an Advisor
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
